
/**
 * Tries to be an inclusive, accessible tabbed content element
 *
 * @sample:
 *
 * <div class="tabbed js-tabbed">
    <ul class="tabbed__tabs">
      <li class="tabbed__tab"><a href="#tab1">Tab 1</a></li>
      <li class="tabbed__tab"><a href="#tab2">Tab 2</a></li>
    </ul>
    <section class="tabbed__panel" id="tab1">I am tab 1</section>
    <section class="tabbed__panel" id="tab2">I am tab 2</section>
   </div>
 */
const tabbed = (() => {

  /**
   * Switches tab
   * @private
   *
   * @param {HTMLElement} oldTab - Old tab
   * @param {HTMLElement} newTab - New tab
   * @param {NodeList} tabs - All tabs
   * @param {NodeList} panels - All panels
   */
  const switchTab = (oldTab, newTab, tabs, panels) => {
    newTab.focus();
    // Make the active tab focusable by the user (Tab key)
    newTab.removeAttribute('tabindex');
    // Set the selected state
    newTab.setAttribute('aria-selected', 'true');
    oldTab.removeAttribute('aria-selected');
    oldTab.setAttribute('tabindex', '-1');
    // Get the indexes of the new and old tabs to find the correct tab panels to show and hide
    const index = tabs.indexOf(newTab);
    const oldIndex = tabs.indexOf(oldTab);

    panels[oldIndex].hidden = true;
    panels[index].hidden = false;
  }

  /**
   * @private
   *
   * @param {HTMLElement} tabbed - Tabbed element container
   */
  const initializeTabs = (tabbed) => {
    // Get relevant elements and collections
    const tablist = tabbed.querySelector('.tabbed__tabs');
    const tabs = Array.from(tablist.querySelectorAll('a'));
    const panels = Array.from(tabbed.querySelectorAll('.tabbed__panel'));

    // Add the tablist role to the nav
    tablist.setAttribute('role', 'tablist');

    // Add semantics and remove user focusability for each tab
    tabs.forEach((tab, i) => {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('id', 'tab' + (i + 1));
      tab.setAttribute('tabindex', '-1');
      tab.parentNode.setAttribute('role', 'presentation');

      // Handle clicking of tabs for mouse users
      tab.addEventListener('click', e => {
        e.preventDefault();
        let currentTab = tablist.querySelector('[aria-selected]');
        if(e.currentTarget !== currentTab) {
          switchTab(currentTab, e.currentTarget, tabs, panels);
        }
      });

      // Handle keydown events for keyboard users
      tab.addEventListener('keydown', e => {
        // Get the index of the current tab in the tabs node list
        let index = Array.prototype.indexOf.call(tabs, e.currentTarget);
        // Work out which key the user is pressing and
        // Calculate the new tab's index where appropriate
        let dir = e.which === 37 ? index - 1 : e.which === 39 ? index + 1 : e.which === 40 ? 'down' : null;
        if(dir !== null) {
          e.preventDefault();
          // If the down key is pressed, move focus to the open panel,
          // otherwise switch to the adjacent tab
          dir === 'down' ? panels[i].focus() : tabs[dir] ? switchTab(e.currentTarget, tabs[dir], tabs, panels) : void 0;
        }
      });
    });

    // Add tab panel semantics and hide them all
    panels.forEach((panel, i) => {
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('tabindex', '-1');
      panel.setAttribute('aria-labelledby', tabs[i].id);
      panel.hidden = true;
    });

    // Initially activate the first tab and reveal the first tab panel
    tabs[0].removeAttribute('tabindex');
    tabs[0].setAttribute('aria-selected', 'true');
    panels[0].hidden = false;

  };

  /**
   * Initializes all tabbed elements with given class
   *
   * @public
   */
  const init = () => {
    const triggers = document.getElementsByClassName('js-tabbed');

    triggers.forEach(triggerEl => {
      initializeTabs(triggerEl);
    })
  };

  return {
    init
  };

})();

export default tabbed;
